import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState, type RefObject } from "react";
import { Plus, FolderSync, Trash2, MoreHorizontal } from "lucide-react";
import {
  ActionMenu,
  Button,
  DataTable,
  Mono,
  PageContent,
  PageHeader,
  PageShell,
  TableIdentity,
  localTablePage,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
  type TableSort,
} from "@h2j-data-team/ui";
import { ConfirmDialog, useConfirmDialog } from "@/components/shared";
import { DensityToggle } from "@/components/density-toggle";
import { supportCode } from "@/components/error-screen";
import { CreateSyncGroupDrawer } from "@/features/groups/components/create-sync-group-drawer";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import {
  groupsQueryOptions,
  useDeleteGroup,
  useAuditAllGroups,
  useSyncAllGroups,
  type GroupWithCounts,
} from "@/features/groups/api";

export const Route = createFileRoute("/_authenticated/sync-groups/")({
  staticData: { pageTitle: "breadcrumbs.syncGroups" },
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  component: SyncGroupsPage,
});

/** Row menu. The delete dialog opens from a menu item that is gone by then, so focus goes back to the kebab. */
function GroupRowActions({
  group,
  disabled,
  onDelete,
}: {
  group: GroupWithCounts;
  disabled: boolean;
  onDelete: (group: GroupWithCounts, trigger: RefObject<HTMLElement | null>) => void;
}) {
  const { t } = useTranslation("sync-groups");
  const trigger = useRef<HTMLButtonElement>(null);
  const label = t("list.actionsFor", { name: group.name });
  return (
    <ActionMenu
      label={label}
      trigger={
        <Button ref={trigger} variant="ghost" size="icon-sm" aria-label={label} title={label}>
          <MoreHorizontal />
        </Button>
      }
      items={[
        {
          id: "delete",
          label: t("list.deleteGroup"),
          icon: <Trash2 />,
          destructive: true,
          disabled,
          onSelect: () => onDelete(group, trigger),
        },
      ]}
    />
  );
}

function SyncGroupsPage() {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const { density, setDensity } = useTableDensity();
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const groupsQuery = useQuery(groupsQueryOptions());
  const groups = groupsQuery.data?.data ?? [];
  const deleteMutation = useDeleteGroup();
  const auditAllMutation = useAuditAllGroups();
  const syncAllMutation = useSyncAllGroups();
  const { confirm, dialogProps } = useConfirmDialog();
  const [createOpen, setCreateOpen] = useState(false);
  const newButton = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  // The list is whole on the client: the search lives in the URL, page, size and sort here.
  const [view, setView] = useState<{ page: number; pageSize: number; sort?: TableSort }>({
    page: 1,
    pageSize: 50,
  });
  const table: DataTableState = { ...view, query: q ?? "", filters: {}, selection: [] };
  const onTableChange = (next: DataTableState) => {
    setView({ page: next.page, pageSize: next.pageSize, sort: next.sort });
    if (next.query !== (q ?? "")) {
      navigate({ search: (prev) => ({ ...prev, q: next.query || undefined }), replace: true });
    }
  };

  const handleDelete = (group: GroupWithCounts, trigger: RefObject<HTMLElement | null>) => {
    confirm({
      title: t("list.deleteTitle"),
      description: t("list.deleteDescription", { name: group.name }),
      confirmLabel: t("list.deleteConfirm"),
      variant: "destructive",
      restoreFocusRef: trigger,
      onConfirm: async () => {
        await deleteMutation.mutateAsync(group.id);
      },
    });
  };

  const handleAuditAll = async () => {
    const job = await auditAllMutation.mutateAsync();
    navigate({ to: "/jobs/$id", params: { id: job.id } });
  };

  const handleSyncAll = async () => {
    const job = await syncAllMutation.mutateAsync();
    navigate({ to: "/jobs/$id", params: { id: job.id } });
  };

  const columns: DataTableColumn<GroupWithCounts>[] = [
    {
      id: "name",
      header: t("table.name"),
      sort: "text",
      value: (group) => group.name,
      rowHeader: true,
      rowLink: true,
      variant: "identity",
      cell: (group) => (
        <TableIdentity
          leading={<FolderSync />}
          name={
            <Link
              to="/sync-groups/$groupId"
              params={{ groupId: group.id }}
              className="hover:underline"
            >
              {group.name}
            </Link>
          }
          detail={group.description || undefined}
          detailMono={false}
        />
      ),
    },
    {
      id: "apps",
      header: t("table.apps"),
      sort: "number",
      value: (group) => group.appCount,
      align: "end",
      variant: "numeric",
      width: 100,
      cell: (group) => group.appCount,
    },
    {
      id: "variables",
      header: t("table.variables"),
      sort: "number",
      value: (group) => group.variableCount,
      align: "end",
      variant: "numeric",
      width: 110,
      cell: (group) => group.variableCount,
    },
    {
      id: "dimensions",
      header: t("table.dimensions"),
      sort: "number",
      value: (group) => group.dimensionCount,
      align: "end",
      variant: "numeric",
      width: 120,
      cell: (group) => group.dimensionCount,
    },
    {
      id: "measures",
      header: t("table.measures"),
      sort: "number",
      value: (group) => group.measureCount,
      align: "end",
      variant: "numeric",
      width: 110,
      cell: (group) => group.measureCount,
    },
  ];

  // Only the name is searched, as before; the count columns have no text to match.
  const page = localTablePage(groups, table, { columns, search: (group) => group.name });
  const code = groupsQuery.isError ? supportCode(groupsQuery.error, tc("error.supportCode")) : null;

  return (
    <PageShell
      header={
        <PageHeader
          title={t("list.pageTitle")}
          description={t("list.description")}
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAuditAll}
                loading={auditAllMutation.isPending}
                disabled={syncAllMutation.isPending}
              >
                {t("list.auditAll")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncAll}
                loading={syncAllMutation.isPending}
                disabled={auditAllMutation.isPending}
              >
                {t("list.syncAll")}
              </Button>
              <Button ref={newButton} size="sm" onClick={() => setCreateOpen(true)}>
                <Plus />
                {t("list.newGroup")}
              </Button>
            </>
          }
        />
      }
    >
      <PageContent fill>
        <DataTable
          label={t("list.pageTitle")}
          columns={columns}
          rows={page.rows}
          getRowId={(group) => group.id}
          state={table}
          onStateChange={onTableChange}
          density={density}
          loading={groupsQuery.isLoading}
          refreshing={listRefreshing(groupsQuery)}
          error={
            groupsQuery.isError
              ? {
                  title: t("list.loadFailedTitle"),
                  description: (
                    <>
                      {t("list.loadFailedDescription")}
                      {code && (
                        <>
                          {" "}
                          {code.label}: <Mono>{code.value}</Mono>
                        </>
                      )}
                    </>
                  ),
                }
              : null
          }
          onRetry={() => void groupsQuery.refetch()}
          search={{
            ...tableSearch(t("list.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
            debounceMs: 300,
          }}
          toolbar={
            <div className="ml-auto">
              <DensityToggle value={density} onChange={setDensity} />
            </div>
          }
          pagination={{
            kind: "offset",
            total: page.total,
            pageSizeOptions: [25, 50, 100],
            labels: paginationLabels(),
          }}
          rowActions={{
            label: t("table.actions"),
            width: 56,
            render: (group) => (
              <GroupRowActions
                group={group}
                disabled={deleteMutation.isPending}
                onDelete={handleDelete}
              />
            ),
          }}
          messages={tableMessages({
            empty: {
              icon: <FolderSync />,
              title: t("list.emptyTitle"),
              description: t("list.emptyDescription"),
              action: (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus />
                  {t("list.emptyAction")}
                </Button>
              ),
            },
            noResults: {
              title: t("list.noMatchTitle"),
              description: t("list.noMatchDescription"),
              action: (
                <Button
                  variant="outline"
                  onClick={() => onTableChange(updateTableState(table, { query: "" }))}
                >
                  {t("list.clearSearch")}
                </Button>
              ),
            },
          })}
        />
      </PageContent>

      <CreateSyncGroupDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        restoreFocusRef={newButton}
      />

      <ConfirmDialog {...dialogProps} isPending={deleteMutation.isPending} />
    </PageShell>
  );
}
